// process-group-reaping.ts
//
// TERM → poll → KILL → poll for a POSIX process group, shared by the worker
// spawner (claude-process-group-spawner.ts) and the verification/deploy
// recipe runner (verification-recipe-runner.ts, Codex review CXR-01). Polls
// DURING the grace window instead of blind-sleeping it: a group that already
// exited returns in ~one tick, a stuck one still gets the full grace before
// SIGKILL.
const POLL_INTERVAL_MS = 50;
const REAP_POLL_MAX_MS = 5000;

export function isProcessGroupAlive(pgid: number): boolean {
  try {
    process.kill(-pgid, 0);
    return true;
  } catch (err) {
    // ESRCH = no such process/group -> reaped. Any other error, treat as
    // "can't tell, assume alive" to avoid false-positive reap.
    return !(
      err instanceof Error &&
      "code" in err &&
      (err as NodeJS.ErrnoException).code === "ESRCH"
    );
  }
}

export async function pollUntilReaped(
  pgid: number,
  sleep: (ms: number) => Promise<void>,
  maxMs: number = REAP_POLL_MAX_MS,
): Promise<boolean> {
  const deadline = Date.now() + maxMs;
  while (Date.now() < deadline) {
    if (!isProcessGroupAlive(pgid)) return true;
    await sleep(POLL_INTERVAL_MS);
  }
  return !isProcessGroupAlive(pgid);
}

/** Sends `sig` to the whole group; a group that is already gone is not an error. */
export function signalProcessGroup(pgid: number, sig: NodeJS.Signals): void {
  if (!Number.isInteger(pgid) || pgid <= 1) return;
  try {
    process.kill(-pgid, sig);
  } catch {
    // already gone
  }
}

/** TERM the group, wait up to `graceMs`, KILL what is left, wait for it to be reaped. */
export async function terminateProcessGroup(
  pgid: number,
  graceMs: number,
  sleep: (ms: number) => Promise<void>,
): Promise<{ groupReaped: boolean }> {
  signalProcessGroup(pgid, "SIGTERM");
  if (await pollUntilReaped(pgid, sleep, graceMs)) return { groupReaped: true };
  signalProcessGroup(pgid, "SIGKILL");
  return { groupReaped: await pollUntilReaped(pgid, sleep) };
}
